import Phaser from "phaser";
import { scenarioById } from "../config/campaign";
import { ENEMIES } from "../config/enemies";
import { CASTLE_APPROACH, MAPS } from "../config/map";
import { DEBUG_RENDER_DEFAULTS, RENDER_DEPTH } from "../config/rendering";
import { TOWERS, towerStatsForInstance } from "../config/towers";
import { events } from "../core/events";
import { DefenseGameState } from "../core/game-state";
import type { DamageType, EnemyInstance, GridCoordinate, TowerInstance } from "../domain/types";
import { AudioManager } from "../systems/audio";
import { defenseAnalytics } from "../systems/analytics";
import { loadSave } from "../systems/save";
import { EffectPool } from "../views/effect-pool";
import { defenseMultiplayer } from "../systems/multiplayer-client";
import { canDeployOnCell, type MultiplayerBattleContext } from "../systems/multiplayer-rules";

interface AmbientDecoration {
  sprite: Phaser.GameObjects.Image;
  cellX: number;
  cellY: number;
  scale: number;
}

interface EnemyViewData {
  image: Phaser.GameObjects.Image;
  shadow: Phaser.GameObjects.Ellipse;
  healthBack: Phaser.GameObjects.Rectangle;
  health: Phaser.GameObjects.Rectangle;
  lastX: number;
  lastHitAt: number;
}

export class GameScene extends Phaser.Scene {
  state!: DefenseGameState;
  private background!: Phaser.GameObjects.Image;
  private ground!: Phaser.GameObjects.Graphics;
  private placement!: Phaser.GameObjects.Graphics;
  private selection!: Phaser.GameObjects.Graphics;
  private hover!: Phaser.GameObjects.Graphics;
  private ghostTower!: Phaser.GameObjects.Image;
  private entrance!: Phaser.GameObjects.Image;
  private entranceBanner!: Phaser.GameObjects.Image;
  private exitBanner!: Phaser.GameObjects.Image;
  private entranceLabel!: Phaser.GameObjects.Text;
  private exitLabel!: Phaser.GameObjects.Text;
  private torches: Phaser.GameObjects.Image[] = [];
  private ambientDecorations: AmbientDecoration[] = [];
  private tile = 36;
  private offsetX = 0;
  private offsetY = 60;
  private towerSprites = new Map<string, Phaser.GameObjects.Container>();
  private enemySprites = new Map<string, Phaser.GameObjects.Container>();
  private unsubscribers: (() => void)[] = [];
  private audio!: AudioManager;
  private settings = loadSave().settings;
  private projectiles!: EffectPool;
  private resultsTransition: Phaser.Time.TimerEvent | null = null;
  private multiplayer: MultiplayerBattleContext | null = null;
  private multiplayerTowerIds = new Map<string, string>();
  private multiplayerRevision = -1;

  constructor() {
    super("Game");
  }

  create(): void {
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);
    this.multiplayer =
      (this.registry.get("multiplayer-battle-context") as MultiplayerBattleContext | undefined) ??
      null;
    const selectedMapId = this.registry.get("selected-map-id") as string | undefined;
    const selectedScenarioId = this.registry.get("selected-scenario-id") as string | undefined;
    const selectedMap = selectedMapId ? MAPS[selectedMapId] : undefined;
    const selectedScenario = scenarioById(selectedScenarioId);
    const loadedTowerIds =
      (this.registry.get("selected-loadout") as readonly string[] | undefined) ??
      Object.keys(TOWERS).slice(0, 5);
    this.state = new DefenseGameState(
      selectedMap ?? CASTLE_APPROACH,
      selectedScenario,
      loadedTowerIds,
    );
    this.registry.set("defense-state", this.state);
    this.registry.set("debug-grid", DEBUG_RENDER_DEFAULTS.grid);
    this.registry.set("debug-path", DEBUG_RENDER_DEFAULTS.navigationPath);
    this.settings = loadSave().settings;
    this.audio = new AudioManager(this.settings);
    this.audio.bindUnlock(this);
    this.audio.playMusic("battle");
    defenseAnalytics.track("battle_scene_started", { mapId: this.state.map.id });
    this.background = this.add
      .image(0, 0, this.state.map.backgroundAsset)
      .setOrigin(0)
      .setDepth(RENDER_DEPTH.background);
    this.ground = this.add.graphics().setDepth(RENDER_DEPTH.groundDecals);
    this.placement = this.add.graphics().setDepth(RENDER_DEPTH.placement);
    this.selection = this.add.graphics().setDepth(RENDER_DEPTH.selection);
    this.hover = this.add.graphics().setDepth(RENDER_DEPTH.selection + 1);
    this.ghostTower = this.add
      .image(0, 0, "tower-archer")
      .setAlpha(0.56)
      .setVisible(false)
      .setDepth(RENDER_DEPTH.selection + 2);
    this.entrance = this.add.image(0, 0, "landmark-entry").setDepth(RENDER_DEPTH.environmentalBack);
    this.entranceBanner = this.add
      .image(0, 0, "map-banner")
      .setDepth(RENDER_DEPTH.environmentalBack + 1);
    this.exitBanner = this.add
      .image(0, 0, "map-banner")
      .setFlipX(true)
      .setDepth(RENDER_DEPTH.environmentalBack + 1);
    this.entranceLabel = this.worldLabel("INVADER ROAD");
    this.exitLabel = this.worldLabel("LION GATE");
    this.torches = [this.add.image(0, 0, "map-torch"), this.add.image(0, 0, "map-torch")];
    for (const torch of this.torches) {
      torch.setDepth(RENDER_DEPTH.combatEffects);
      this.tweens.add({
        targets: torch,
        alpha: { from: 0.68, to: 1 },
        scaleY: { from: 0.92, to: 1.08 },
        duration: 380,
        yoyo: true,
        repeat: -1,
        delay: Math.random() * 160,
      });
    }
    this.projectiles = new EffectPool(this, RENDER_DEPTH.projectiles);
    this.layout();
    this.drawGround();
    this.drawPlacementOverlay();
    this.createAmbientMotion();
    this.createBattlefieldAtmosphere();
    this.input.mouse?.disableContextMenu();
    this.input.on("pointermove", this.updateHover, this);
    this.input.on("pointerdown", this.handlePointerDown, this);
    this.scale.on("resize", this.resize, this);
    this.unsubscribers.push(
      events.on("navigation:changed", () => {
        this.drawGround();
        this.drawPlacementOverlay();
      }),
      events.on("tower:selected", () => this.drawSelection()),
      events.on("build:selected", () => {
        this.drawPlacementOverlay();
        this.hover.clear();
        this.ghostTower.setVisible(false);
      }),
      events.on("tower:placed", ({ towerId }) => {
        const tower = this.state.towers.get(towerId);
        if (tower) this.addTowerSprite(tower, true);
      }),
      events.on("tower:upgraded", ({ towerId, level }) => this.upgradeEffect(towerId, level)),
      events.on("combat:fired", (event) => this.attackEffect(event.towerId, event.enemyIds)),
      events.on("wave:started", ({ wave }) => {
        if (this.multiplayer?.isHost)
          this.sendMultiplayerCommand({ action: "defense-set-wave", wave });
      }),
      events.on("enemy:spawned", () => this.spawnTelegraph()),
      events.on("enemy:hit", (event) => this.hitEffect(event.enemyId, event.type)),
      events.on("enemy:destroyed", (event) =>
        this.deathEffect(event.enemyId, event.type, event.durationMs),
      ),
      events.on("enemy:escaped", (event) => this.escapeEffect(event.enemyId, event.durationMs)),
      events.on("enemy:removed", ({ enemyId }) => this.removeEnemySprite(enemyId)),
      events.on("game:ended", () => this.scheduleResults()),
      events.on("game:restart-requested", () => this.scene.restart()),
      events.on("game:exit-requested", () => this.scene.start("MainMenu")),
    );
    if (this.multiplayer) {
      this.unsubscribers.push(
        defenseMultiplayer.subscribe(() => this.reconcileMultiplayerSnapshot()),
      );
      this.reconcileMultiplayerSnapshot();
    }
    this.scene.launch("UI");
    this.input.keyboard?.on("keydown-G", this.toggleDebugGrid, this);
    this.input.keyboard?.on("keydown-P", this.toggleDebugPath, this);
    this.input.keyboard?.on("keydown-T", this.stressSpawn, this);
    document.addEventListener("visibilitychange", this.visibilityChanged);
  }

  override update(time: number, delta: number): void {
    defenseAnalytics.sampleFrame(delta);
    this.state.update(Math.min(delta, 80) * this.state.speed);
    this.syncSprites(time);
  }

  shutdown(): void {
    for (const unsubscribe of this.unsubscribers) unsubscribe();
    this.unsubscribers = [];
    this.resultsTransition?.remove(false);
    this.resultsTransition = null;
    this.scale.off("resize", this.resize, this);
    this.input.off("pointermove", this.updateHover, this);
    this.input.off("pointerdown", this.handlePointerDown, this);
    this.input.keyboard?.off("keydown-G", this.toggleDebugGrid, this);
    this.input.keyboard?.off("keydown-P", this.toggleDebugPath, this);
    this.input.keyboard?.off("keydown-T", this.stressSpawn, this);
    this.tweens.killAll();
    this.time.removeAllEvents();
    this.projectiles.destroy();
    this.towerSprites.clear();
    this.enemySprites.clear();
    this.multiplayerTowerIds.clear();
    this.ambientDecorations = [];
    document.removeEventListener("visibilitychange", this.visibilityChanged);
    this.scene.stop("UI");
    this.registry.remove("multiplayer-battle-context");
  }

  cellCenter(coordinate: GridCoordinate): { x: number; y: number } {
    return {
      x: this.offsetX + (coordinate.x + 0.5) * this.tile,
      y: this.offsetY + (coordinate.y + 0.5) * this.tile,
    };
  }

  private createBattlefieldAtmosphere(): void {
    const width = this.scale.width;
    const height = this.scale.height;
    const vignette = this.add.graphics().setDepth(RENDER_DEPTH.healthBars - 2);
    vignette.fillStyle(0x000000, 0.12);
    vignette.fillRect(0, 0, width, 18);
    vignette.fillRect(0, height - 18, width, 18);
    vignette.fillRect(0, 0, 14, height);
    vignette.fillRect(width - 14, 0, 14, height);

    if (this.settings.reducedParticles) return;
    for (let index = 0; index < 12; index += 1) {
      const fleck = this.add
        .circle(
          Phaser.Math.Between(20, Math.max(21, width - 20)),
          Phaser.Math.Between(this.offsetY, Math.max(this.offsetY + 1, height - 90)),
          Phaser.Math.FloatBetween(0.7, 1.6),
          Phaser.Math.RND.pick([0xe7c66d, 0xe7ead1, 0xc6d890]),
          Phaser.Math.FloatBetween(0.08, 0.2),
        )
        .setDepth(RENDER_DEPTH.combatEffects - 1);
      this.tweens.add({
        targets: fleck,
        x: fleck.x + Phaser.Math.Between(-24, 24),
        y: fleck.y - Phaser.Math.Between(28, 75),
        alpha: 0,
        duration: Phaser.Math.Between(2600, 4800),
        delay: Phaser.Math.Between(0, 1800),
        repeat: -1,
      });
    }
  }

  private worldLabel(value: string): Phaser.GameObjects.Text {
    return this.add
      .text(0, 0, value, {
        fontFamily: "Georgia, serif",
        fontSize: "12px",
        fontStyle: "bold",
        color: "#fff0bd",
        backgroundColor: "#241b14cc",
        padding: { x: 6, y: 3 },
      })
      .setOrigin(0.5)
      .setDepth(RENDER_DEPTH.healthBars);
  }

  private layout(): void {
    const availableWidth = this.scale.width - 18,
      availableHeight = this.scale.height - (this.scale.width < 700 ? 220 : 175);
    this.tile = Math.max(
      10,
      Math.floor(
        Math.min(availableWidth / this.state.map.width, availableHeight / this.state.map.height),
      ),
    );
    this.offsetX = Math.floor((this.scale.width - this.tile * this.state.map.width) / 2);
    this.offsetY = this.scale.width < 700 ? 64 : 56;
    this.background
      .setPosition(this.offsetX, this.offsetY)
      .setDisplaySize(this.tile * this.state.map.width, this.tile * this.state.map.height);
    const entry = this.cellCenter(this.state.map.entrances[0]!.cell),
      exit = this.cellCenter(this.state.map.exits[0]!.cell),
      landmarkScale = this.tile / 48;
    this.entrance
      .setPosition(entry.x - this.tile * 0.3, entry.y)
      .setScale(Math.max(0.5, landmarkScale));
    this.entranceBanner
      .setPosition(entry.x + this.tile * 0.75, entry.y - this.tile * 1.05)
      .setScale(Math.max(0.45, landmarkScale));
    this.exitBanner
      .setPosition(exit.x - this.tile * 0.85, exit.y - this.tile * 1.1)
      .setScale(Math.max(0.45, landmarkScale));
    this.entranceLabel
      .setPosition(entry.x + this.tile * 0.8, entry.y + this.tile * 0.72)
      .setScale(Math.max(0.65, landmarkScale));
    this.exitLabel
      .setPosition(exit.x - this.tile * 0.85, exit.y + this.tile * 0.75)
      .setScale(Math.max(0.65, landmarkScale));
    this.torches[0]
      ?.setPosition(exit.x - this.tile * 0.42, exit.y - this.tile * 0.56)
      .setScale(Math.max(0.45, landmarkScale));
    this.torches[1]
      ?.setPosition(exit.x - this.tile * 0.42, exit.y + this.tile * 0.5)
      .setScale(Math.max(0.45, landmarkScale));
    this.layoutAmbientDecorations();
  }

  private resize(): void {
    this.layout();
    this.drawGround();
    this.drawPlacementOverlay();
    for (const [id, sprite] of this.towerSprites) {
      const tower = this.state.towers.get(id);
      if (tower) this.layoutTower(sprite, tower);
    }
    this.drawSelection();
  }

  private drawGround(): void {
    this.ground.clear();
    const entrance = this.state.map.entrances[0],
      route = entrance ? this.state.navigation.routeFrom(entrance.cell) : [];
    if (route.length > 1) {
      this.ground
        .lineStyle(Math.max(4, this.tile * 0.3), 0x9a7447, this.state.waves.running ? 0.16 : 0.09)
        .beginPath();
      route.forEach((cell, index) => {
        const p = this.cellCenter(cell);
        if (index === 0) this.ground.moveTo(p.x, p.y);
        else this.ground.lineTo(p.x, p.y);
      });
      this.ground.strokePath();
    }
    if (this.registry.get("debug-grid"))
      for (const cell of this.state.grid.cells)
        this.ground
          .lineStyle(1, 0xeaf6e7, 0.35)
          .strokeRect(
            this.offsetX + cell.x * this.tile,
            this.offsetY + cell.y * this.tile,
            this.tile,
            this.tile,
          );
    if (this.registry.get("debug-path") && route.length) {
      this.ground.lineStyle(3, 0x62d9ff, 0.9).beginPath();
      route.forEach((cell, index) => {
        const p = this.cellCenter(cell);
        if (index === 0) this.ground.moveTo(p.x, p.y);
        else this.ground.lineTo(p.x, p.y);
      });
      this.ground.strokePath();
    }
  }

  private drawPlacementOverlay(): void {
    this.placement.clear();
    const definitionId = this.state.selectedBuild,
      definition = definitionId ? TOWERS[definitionId] : null;
    if (!definition) return;
    for (const cell of this.state.grid.cells) {
      const check = this.state.grid.basicPlacementCheck(cell, definition.footprint),
        x = this.offsetX + cell.x * this.tile,
        y = this.offsetY + cell.y * this.tile;
      if (check.valid)
        this.placement
          .fillStyle(0xc8db9d, 0.17)
          .fillCircle(x + this.tile / 2, y + this.tile / 2, Math.max(1.5, this.tile * 0.07));
      else if (cell.obstacleId || cell.protectedRoute || cell.isEntrance || cell.isExit) {
        this.placement
          .fillStyle(0x7c342e, 0.045)
          .fillRect(x + 2, y + 2, this.tile - 4, this.tile - 4);
        this.placement
          .lineStyle(1, 0xd8aa91, 0.13)
          .lineBetween(x + 5, y + 5, x + this.tile - 5, y + this.tile - 5);
      }
      this.placement.lineStyle(1, 0xf4e7bd, 0.025).strokeRect(x, y, this.tile, this.tile);
    }
  }

  private drawSelection(): void {
    this.selection.clear();
    const tower = this.state.selectedTower ? this.state.towers.get(this.state.selectedTower) : null,
      definition = tower ? TOWERS[tower.definitionId] : null;
    if (!tower || !definition) return;
    const point = this.cellCenter(tower.origin),
      range = towerStatsForInstance(definition, tower).range * this.tile;
    this.selection
      .fillStyle(0xf1d27a, 0.07)
      .fillCircle(point.x, point.y, range)
      .lineStyle(2, 0xf1d27a, 0.72)
      .strokeCircle(point.x, point.y, range)
      .fillStyle(0xffedaa, 0.4)
      .fillCircle(point.x, point.y, this.tile * 0.42);
  }

  private pointerCell(pointer: Phaser.Input.Pointer): GridCoordinate {
    return {
      x: Math.floor((pointer.x - this.offsetX) / this.tile),
      y: Math.floor((pointer.y - this.offsetY) / this.tile),
    };
  }
  private insideBoard(pointer: Phaser.Input.Pointer): boolean {
    return this.state.grid.inside(this.pointerCell(pointer));
  }

  private updateHover(pointer: Phaser.Input.Pointer): void {
    this.hover.clear();
    this.ghostTower.setVisible(false);
    const definitionId = this.state.selectedBuild;
    if (!definitionId || !this.insideBoard(pointer)) return;
    const cell = this.pointerCell(pointer),
      point = this.cellCenter(cell),
      result = this.state.previewPlacement(definitionId, cell),
      definition = TOWERS[definitionId];
    if (!definition) return;
    const ownsCell =
      !this.multiplayer || canDeployOnCell(this.multiplayer, cell, this.state.map.width);
    const placementValid = result.valid && ownsCell;
    const color = placementValid ? 0x9dd67c : 0xe46b5d;
    this.hover
      .fillStyle(color, 0.28)
      .fillRoundedRect(
        point.x - this.tile / 2 + 2,
        point.y - this.tile / 2 + 2,
        this.tile - 4,
        this.tile - 4,
        5,
      )
      .lineStyle(3, color, 0.95)
      .strokeRoundedRect(
        point.x - this.tile / 2 + 2,
        point.y - this.tile / 2 + 2,
        this.tile - 4,
        this.tile - 4,
        5,
      );
    if (placementValid) {
      this.hover
        .lineStyle(2, color, 0.7)
        .strokeCircle(point.x, point.y, definition.range * this.tile);
      this.hover
        .lineStyle(3, 0xf5edcf, 0.95)
        .lineBetween(point.x - 6, point.y, point.x - 1, point.y + 6)
        .lineBetween(point.x - 1, point.y + 6, point.x + 8, point.y - 7);
    } else
      this.hover
        .lineStyle(4, 0xffe1d6, 0.95)
        .lineBetween(point.x - 8, point.y - 8, point.x + 8, point.y + 8)
        .lineBetween(point.x + 8, point.y - 8, point.x - 8, point.y + 8);
    this.ghostTower
      .setTexture(definition.texture)
      .setPosition(point.x, point.y)
      .setDisplaySize(this.tile * 1.45, this.tile * 1.35)
      .setTint(placementValid ? 0xffffff : 0xd06055)
      .setVisible(true);
  }

  private handleBoardClick(pointer: Phaser.Input.Pointer): void {
    if (!this.insideBoard(pointer) || pointer.y < this.offsetY) return;
    const cell = this.pointerCell(pointer),
      occupied = this.state.grid.cell(cell)?.towerId;
    if (occupied) {
      this.state.selectTower(occupied);
      return;
    }
    if (this.multiplayer && !canDeployOnCell(this.multiplayer, cell, this.state.map.width)) {
      events.emit("placement:feedback", {
        valid: false,
        message: `You may only deploy on the ${this.multiplayer.localSide} half of the battlefield.`,
      });
      return;
    }
    if (this.state.selectedBuild && this.state.placeTower(this.state.selectedBuild, cell)) {
      if (this.multiplayer) {
        this.sendMultiplayerCommand({
          action: "defense-place-tower",
          towerId: this.state.selectedBuild,
          x: cell.x,
          y: cell.y,
          side: this.multiplayer.localSide,
        });
      }
      this.audio.tone(520, 0.09, "triangle");
      this.drawPlacementOverlay();
    }
  }

  private sendMultiplayerCommand(command: Readonly<Record<string, unknown>>): void {
    try {
      defenseMultiplayer.send({ type: "match:command", command });
    } catch {
      // Reconnect handling will request the latest authoritative snapshot.
    }
  }

  private reconcileMultiplayerSnapshot(): void {
    if (!this.multiplayer) return;
    const snapshot = defenseMultiplayer.matchSnapshot;
    if (!snapshot || snapshot.revision <= this.multiplayerRevision) return;
    this.multiplayerRevision = snapshot.revision;
    const payload = snapshot.payload as Record<string, unknown>;
    const playerEntries = Array.isArray(payload.players)
      ? payload.players.filter(
          (entry): entry is Record<string, unknown> =>
            typeof entry === "object" && entry !== null && !Array.isArray(entry),
        )
      : [];
    const localRecord =
      playerEntries.find((entry) => entry.userId === this.multiplayer?.localPlayerId) ?? {};
    const gold =
      typeof localRecord.gold === "number" ? localRecord.gold : this.state.economy.balance;
    const wave =
      typeof payload.wave === "number" ? payload.wave : this.state.waves.currentWaveNumber;
    const score = typeof payload.sharedScore === "number" ? payload.sharedScore : this.state.score;
    let lives = this.state.lives;
    if (snapshot.mode === "coop" && typeof payload.sharedCastleHealth === "number") {
      lives = payload.sharedCastleHealth;
    } else if (snapshot.mode === "head-to-head") {
      const gates =
        typeof payload.gates === "object" && payload.gates !== null
          ? (payload.gates as Record<string, unknown>)
          : {};
      const gate =
        typeof gates[this.multiplayer.localSide] === "object" &&
        gates[this.multiplayer.localSide] !== null
          ? (gates[this.multiplayer.localSide] as Record<string, unknown>)
          : {};
      if (typeof gate.health === "number") lives = gate.health;
    }
    this.state.syncAuthoritativeSummary(gold, lives, score, wave);

    const authoritative = new Map<string, { definitionId: string; x: number; y: number }>();
    for (const player of playerEntries) {
      const towers = Array.isArray(player.towers) ? player.towers : [];
      for (const towerValue of towers) {
        if (typeof towerValue !== "object" || towerValue === null || Array.isArray(towerValue))
          continue;
        const tower = towerValue as Record<string, unknown>;
        if (
          typeof tower.cellId !== "string" ||
          typeof tower.towerId !== "string" ||
          typeof tower.x !== "number" ||
          typeof tower.y !== "number"
        )
          continue;
        authoritative.set(tower.cellId, {
          definitionId: tower.towerId,
          x: tower.x,
          y: tower.y,
        });
      }
    }

    for (const [cellId, tower] of authoritative) {
      if (this.multiplayerTowerIds.has(cellId)) continue;
      const existing = [...this.state.towers.values()].find(
        (entry) =>
          entry.origin.x === tower.x &&
          entry.origin.y === tower.y &&
          entry.definitionId === tower.definitionId,
      );
      const localId =
        existing?.id ??
        this.state.placeTowerAuthoritative(tower.definitionId, { x: tower.x, y: tower.y });
      if (localId) this.multiplayerTowerIds.set(cellId, localId);
    }
    for (const [cellId, localId] of [...this.multiplayerTowerIds]) {
      if (authoritative.has(cellId)) continue;
      this.state.removeTowerAuthoritative(localId);
      this.multiplayerTowerIds.delete(cellId);
    }
  }

  private handlePointerDown(pointer: Phaser.Input.Pointer): void {
    if (pointer.rightButtonDown()) {
      this.state.selectBuild(null);
      this.hover.clear();
      this.ghostTower.setVisible(false);
      return;
    }

    if (this.pointerIsOverUi(pointer)) return;

    this.updateHover(pointer);
    this.handleBoardClick(pointer);
  }

  private pointerIsOverUi(pointer: Phaser.Input.Pointer): boolean {
    const mobile = this.scale.width < 700;
    const reservedHeight = this.state.selectedTower ? (mobile ? 285 : 235) : mobile ? 130 : 82;
    return pointer.y >= this.scale.height - reservedHeight;
  }

  private addTowerSprite(tower: TowerInstance, animate: boolean): void {
    if (this.towerSprites.has(tower.id)) return;
    const definition = TOWERS[tower.definitionId];
    if (!definition) return;
    const shadow = this.add.ellipse(
        0,
        this.tile * 0.28,
        this.tile * 1.08,
        this.tile * 0.36,
        0x17130f,
        0.32,
      ),
      image = this.add.image(0, 0, `tower-${tower.definitionId}-l${Math.min(5, tower.level)}`),
      rank = this.add
        .text(this.tile * 0.34, -this.tile * 0.35, `${tower.level}`, {
          fontFamily: "Georgia, serif",
          fontSize: "11px",
          fontStyle: "bold",
          color: "#2b2118",
          backgroundColor: "#f0cf69",
          padding: { x: 4, y: 1 },
        })
        .setOrigin(0.5);
    const group = this.add.container(0, 0, [shadow, image, rank]).setDepth(RENDER_DEPTH.units);
    group.setData("image", image);
    group.setData("rank", rank);
    this.towerSprites.set(tower.id, group);
    this.layoutTower(group, tower);
    if (animate) {
      group.setAlpha(0).setScale(0.72);
      this.tweens.add({ targets: group, alpha: 1, scale: 1, duration: 330, ease: "Back.Out" });
      this.pulseAt(group.x, group.y, 0xd7bd75, this.tile * 0.8);
    }
  }

  private layoutTower(group: Phaser.GameObjects.Container, tower: TowerInstance): void {
    const point = this.cellCenter(tower.origin),
      image = group.getData("image") as Phaser.GameObjects.Image;
    group.setPosition(point.x, point.y);
    image.setTexture(`tower-${tower.definitionId}-l${Math.min(5, tower.level)}`);
    image.setDisplaySize(this.tile * 1.62, this.tile * 1.48);
  }

  private syncSprites(time: number): void {
    for (const [towerId, group] of this.towerSprites) {
      const tower = this.state.towers.get(towerId);
      if (!tower || tower.definitionId !== "archer") continue;
      const image = group.getData("image") as Phaser.GameObjects.Image;
      const phase = (Number(towerId.split("-").at(-1)) || 0) * 0.7;
      image.y = Math.sin(time * 0.0018 + phase) * 1.2;
      image.angle = Math.sin(time * 0.0012 + phase) * 0.35;
    }

    for (const enemy of this.state.enemies.values()) {
      if (enemy.state === "pending" || enemy.state === "removed") continue;
      let group = this.enemySprites.get(enemy.id);
      group ??= this.createEnemySprite(enemy);
      const data = group.getData("view") as EnemyViewData,
        definition = ENEMIES[enemy.definitionId];
      if (!definition) continue;
      const visualOffset = this.enemyVisualOffset(enemy);
      group.setPosition(
        this.offsetX + enemy.position.x * this.tile + visualOffset.x,
        this.offsetY + enemy.position.y * this.tile + visualOffset.y,
      );
      if (enemy.state === "spawning") {
        const progress = Math.min(1, enemy.lifecycleElapsedMs / definition.spawnDurationMs);
        group.setAlpha(0.28 + progress * 0.72).setScale(0.88 + progress * 0.12);
        data.image.setY(Math.sin(progress * Math.PI * 3) * 1.5);
        data.image.setAngle(Math.sin(progress * Math.PI * 2) * 4);
        data.healthBack.setVisible(false);
        data.health.setVisible(false);
      } else if (enemy.state === "active") {
        group.setAlpha(1).setScale(1);
        const stridePhase =
          time * 0.014 * definition.movementSpeed + Number(enemy.id.split("-").at(-1) ?? 0);
        const bob = Math.sin(stridePhase) * definition.visual.bobAmount * (this.tile / 48);
        const stride = Math.sin(stridePhase * 0.5);
        data.image.setY(bob);
        data.image.setAngle(stride * (definition.tags.includes("boss") ? 0.7 : 1.8));
        data.shadow.setScale(1 + Math.abs(stride) * 0.04, 1 - Math.abs(stride) * 0.08);
        const dx = enemy.position.x - data.lastX;
        if (Math.abs(dx) > 0.002) data.image.setFlipX(dx < 0);
        data.lastX = enemy.position.x;
        const damaged = enemy.health < (enemy.maximumHealth ?? definition.maximumHealth);
        data.healthBack.setVisible(damaged);
        data.health
          .setVisible(damaged)
          .setDisplaySize(
            (this.tile * 0.78 * enemy.health) / (enemy.maximumHealth ?? definition.maximumHealth),
            Math.max(3, this.tile * 0.08),
          );
      }
    }
    for (const [id, group] of this.enemySprites)
      if (!this.state.enemies.has(id)) {
        group.destroy();
        this.enemySprites.delete(id);
      }
    for (const tower of this.state.towers.values())
      if (!this.towerSprites.has(tower.id)) this.addTowerSprite(tower, false);
    for (const [id, group] of this.towerSprites)
      if (!this.state.towers.has(id)) {
        group.destroy();
        this.towerSprites.delete(id);
      }
  }

  private createEnemySprite(enemy: EnemyInstance): Phaser.GameObjects.Container {
    const definition = ENEMIES[enemy.definitionId]!,
      shadow = this.add.ellipse(
        0,
        this.tile * 0.27,
        this.tile * 0.64 * definition.visual.shadowScale,
        this.tile * 0.22,
        0x12100d,
        0.34,
      ),
      image = this.add
        .image(0, 0, definition.texture)
        .setDisplaySize(this.tile * definition.size, this.tile * definition.size),
      healthBack = this.add.rectangle(
        0,
        -this.tile * 0.52,
        this.tile * 0.82,
        Math.max(4, this.tile * 0.1),
        0x281714,
        0.9,
      ),
      health = this.add
        .rectangle(
          -this.tile * 0.39,
          -this.tile * 0.52,
          this.tile * 0.78,
          Math.max(3, this.tile * 0.08),
          0x79c970,
        )
        .setOrigin(0, 0.5);
    healthBack.setVisible(false);
    health.setVisible(false);
    const group = this.add
      .container(0, 0, [shadow, image, healthBack, health])
      .setDepth(RENDER_DEPTH.units);
    group.setData("view", {
      image,
      shadow,
      healthBack,
      health,
      lastX: enemy.position.x,
      lastHitAt: -1_000,
    } satisfies EnemyViewData);
    this.enemySprites.set(enemy.id, group);
    return group;
  }

  private enemyVisualOffset(enemy: EnemyInstance): { x: number; y: number } {
    const sequence = Number(enemy.id.split("-").at(-1)) || 0,
      column = (sequence % 3) - 1,
      row = (Math.floor(sequence / 3) % 3) - 1;
    return { x: column * this.tile * 0.065, y: row * this.tile * 0.055 };
  }

  private attackEffect(towerId: string, enemyIds: readonly string[]): void {
    const tower = this.state.towers.get(towerId),
      towerGroup = this.towerSprites.get(towerId);
    if (!tower || !towerGroup) return;
    const from = this.cellCenter(tower.origin),
      points = enemyIds
        .map((id) => this.state.enemies.get(id))
        .filter((enemy): enemy is EnemyInstance => Boolean(enemy))
        .map((enemy) => ({
          x: this.offsetX + enemy.position.x * this.tile,
          y: this.offsetY + enemy.position.y * this.tile,
        }));
    if (!points.length) return;
    const weapon = towerGroup.getData("image") as Phaser.GameObjects.Image;
    this.tweens.killTweensOf(weapon);
    this.tweens.add({
      targets: weapon,
      y: tower.definitionId === "archer" ? -5 : -3,
      angle: tower.definitionId === "archer" ? -1.5 : 0,
      scaleX: tower.definitionId === "archer" ? 1.08 : 1.06,
      scaleY: tower.definitionId === "archer" ? 0.92 : 0.94,
      duration: tower.definitionId === "archer" ? 85 : 70,
      ease: "Quad.Out",
      yoyo: true,
    });
    if (tower.definitionId === "archer")
      this.pulseAt(from.x, from.y - this.tile * 0.22, 0xe7c97b, this.tile * 0.28);
    if (tower.definitionId === "lightning") {
      const line = this.add.graphics().setDepth(RENDER_DEPTH.projectiles),
        all = [from, ...points];
      line.lineStyle(Math.max(2, this.tile * 0.08), 0xbdeaff, 0.95).beginPath();
      all.forEach((point, index) => {
        if (index === 0) line.moveTo(point.x, point.y);
        else {
          const previous = all[index - 1]!;
          for (let part = 1; part <= 4; part += 1) {
            const t = part / 4,
              jitter = part === 4 ? 0 : part % 2 ? 5 : -5;
            line.lineTo(
              previous.x + (point.x - previous.x) * t,
              previous.y + (point.y - previous.y) * t + jitter,
            );
          }
        }
      });
      line.strokePath();
      this.tweens.add({ targets: line, alpha: 0, duration: 170, onComplete: () => line.destroy() });
      points.forEach((point) => this.pulseImpact(point, 0xa9e8ff));
    } else {
      const texture =
        tower.definitionId === "rocket"
          ? "projectile-rocket"
          : tower.definitionId === "cannon"
            ? "projectile-cannon"
            : "projectile-arrow";
      for (const point of points) this.flyProjectile(texture, from, point, tower.definitionId);
    }
    this.audio.tone(
      tower.definitionId === "cannon" ? 110 : 380,
      0.06,
      tower.definitionId === "lightning" ? "sawtooth" : "square",
    );
  }

  private flyProjectile(
    texture: string,
    from: { x: number; y: number },
    to: { x: number; y: number },
    towerType: string,
  ): void {
    const image = this.projectiles.acquire(texture),
      distance = Phaser.Math.Distance.Between(from.x, from.y, to.x, to.y),
      duration = Phaser.Math.Clamp(
        distance * (towerType === "cannon" ? 1.8 : towerType === "rocket" ? 1.35 : 0.65),
        90,
        520,
      );
    image
      .setPosition(from.x, from.y)
      .setRotation(Phaser.Math.Angle.Between(from.x, from.y, to.x, to.y))
      .setScale(Math.max(0.55, this.tile / 48));
    this.tweens.add({
      targets: image,
      x: to.x,
      y: to.y,
      duration,
      ease: towerType === "cannon" ? "Sine.In" : "Linear",
      onComplete: () => {
        this.projectiles.release(image);
        this.pulseImpact(
          to,
          towerType === "rocket" ? 0xff8a43 : towerType === "cannon" ? 0xc9ad7a : 0xf0dba0,
          towerType !== "archer",
        );
      },
    });
  }

  private hitEffect(enemyId: string, type: DamageType): void {
    const group = this.enemySprites.get(enemyId);
    if (!group) return;
    const data = group.getData("view") as EnemyViewData,
      now = this.time.now;
    if (now - data.lastHitAt < 70) return;
    data.lastHitAt = now;
    data.image.setTint(
      type === "electrical" ? 0xc8f2ff : type === "explosive" ? 0xffc07a : 0xffffff,
    );
    this.time.delayedCall(65, () => data.image.active && data.image.clearTint());
    this.tweens.add({ targets: data.image, x: 2.5, duration: 38, yoyo: true });
  }

  private deathEffect(id: string, type: DamageType, durationMs: number): void {
    const group = this.enemySprites.get(id);
    if (!group) return;
    const data = group.getData("view") as EnemyViewData;
    data.healthBack.setVisible(false);
    data.health.setVisible(false);
    this.pulseImpact(
      { x: group.x, y: group.y },
      type === "electrical" ? 0xa9e8ff : type === "explosive" ? 0xff8a43 : 0xd6bd85,
      type !== "physical",
    );
    this.tweens.killTweensOf(group);
    this.tweens.add({
      targets: group,
      angle: type === "explosive" ? 22 : 78,
      y: group.y + this.tile * 0.22,
      scaleY: 0.62,
      alpha: 0.12,
      duration: Math.max(180, durationMs * 0.88),
      ease: "Cubic.Out",
    });
  }

  private escapeEffect(id: string, durationMs: number): void {
    const group = this.enemySprites.get(id);
    if (!group) return;
    this.tweens.add({
      targets: group,
      alpha: 0,
      scale: 0.72,
      x: group.x + this.tile * 0.55,
      duration: durationMs,
      ease: "Cubic.In",
    });
    this.flashLifeLoss(group.x, group.y);
  }
  private removeEnemySprite(id: string): void {
    this.enemySprites.get(id)?.destroy();
    this.enemySprites.delete(id);
  }

  private upgradeEffect(towerId: string, level: number): void {
    const group = this.towerSprites.get(towerId);
    if (!group) return;
    const rank = group.getData("rank") as Phaser.GameObjects.Text;
    rank.setText(`${level}`);
    const tower = this.state.towers.get(towerId);
    const image = group.getData("image") as Phaser.GameObjects.Image;
    if (tower) {
      image.setTexture(`tower-${tower.definitionId}-l${Math.min(5, level)}`);
      image.setDisplaySize(this.tile * 1.62, this.tile * 1.48);
    }
    this.tweens.add({ targets: group, scale: 1.16, duration: 150, yoyo: true, ease: "Back.Out" });
    this.pulseAt(group.x, group.y, 0xffdc6b, this.tile * (0.75 + level * 0.08));
  }
  private spawnTelegraph(): void {
    const cell = this.state.map.entrances[0]?.cell;
    if (!cell) return;
    const point = this.cellCenter(cell);
    this.pulseImpact({ x: point.x - this.tile * 0.45, y: point.y }, 0xc8a875, true);
  }
  private pulseImpact(point: { x: number; y: number }, tint: number, large = false): void {
    if (this.settings.reducedParticles && large) return;
    const image = this.projectiles
      .acquire(large ? "effect-dust" : "effect-impact")
      .setPosition(point.x, point.y)
      .setTint(tint)
      .setScale(large ? this.tile / 30 : this.tile / 48);
    this.tweens.add({
      targets: image,
      alpha: 0,
      scale: image.scale * 1.7,
      duration: large ? 360 : 180,
      onComplete: () => this.projectiles.release(image),
    });
  }
  private pulseAt(x: number, y: number, color: number, radius: number): void {
    const ring = this.add
      .graphics()
      .setDepth(RENDER_DEPTH.combatEffects)
      .lineStyle(3, color, 0.8)
      .strokeCircle(x, y, radius * 0.45);
    ring.setScale(0.6);
    this.tweens.add({
      targets: ring,
      scale: 1.35,
      alpha: 0,
      duration: 420,
      onComplete: () => ring.destroy(),
    });
  }
  private flashLifeLoss(x: number, y: number): void {
    const text = this.add
      .text(x, y, "GATE BREACHED!", {
        fontFamily: "Georgia, serif",
        fontSize: "13px",
        fontStyle: "bold",
        color: "#ff8b78",
        stroke: "#2b1713",
        strokeThickness: 4,
      })
      .setOrigin(0.5)
      .setDepth(RENDER_DEPTH.healthBars);
    this.tweens.add({
      targets: text,
      y: y - 30,
      alpha: 0,
      duration: 650,
      onComplete: () => text.destroy(),
    });
  }
  private createAmbientMotion(): void {
    this.tweens.add({
      targets: [this.entranceBanner, this.exitBanner],
      angle: { from: -1.5, to: 1.5 },
      duration: 1_300,
      yoyo: true,
      repeat: -1,
      ease: "Sine.InOut",
    });

    if (this.state.map.id !== "castle-approach") return;

    const grassCells = [
      [2.2, 2.4],
      [3.5, 12.8],
      [7.5, 1.7],
      [8.4, 13.9],
      [14.2, 2.2],
      [20.6, 12.9],
      [21.5, 4.2],
    ] as const;
    for (const [cellX, cellY] of grassCells) {
      const sprite = this.add
        .image(0, 0, "environment-grass-tuft")
        .setDepth(RENDER_DEPTH.environmentalBack + 2);
      this.ambientDecorations.push({ sprite, cellX, cellY, scale: 0.72 });
      this.tweens.add({
        targets: sprite,
        angle: { from: -2.5, to: 2.5 },
        duration: Phaser.Math.Between(1_500, 2_500),
        yoyo: true,
        repeat: -1,
        ease: "Sine.InOut",
      });
    }

    const flowerCells = [
      [1.8, 4.5],
      [5.2, 13.6],
      [9.1, 2.3],
      [15.7, 13.1],
      [20.8, 6.2],
    ] as const;
    for (const [cellX, cellY] of flowerCells) {
      const sprite = this.add
        .image(0, 0, "environment-flower")
        .setDepth(RENDER_DEPTH.environmentalBack + 3);
      this.ambientDecorations.push({ sprite, cellX, cellY, scale: 0.66 });
      this.tweens.add({
        targets: sprite,
        scaleY: { from: 0.62, to: 0.7 },
        duration: Phaser.Math.Between(1_700, 2_700),
        yoyo: true,
        repeat: -1,
        ease: "Sine.InOut",
      });
    }

    const butterfly = this.add
      .image(0, 0, "environment-butterfly")
      .setDepth(RENDER_DEPTH.combatEffects - 1);
    this.ambientDecorations.push({ sprite: butterfly, cellX: 6.2, cellY: 5.1, scale: 0.7 });
    this.layoutAmbientDecorations();
    this.tweens.add({
      targets: butterfly,
      x: `+=${this.tile * 2.2}`,
      y: `-=${this.tile * 0.7}`,
      angle: { from: -6, to: 6 },
      duration: 4_200,
      yoyo: true,
      repeat: -1,
      ease: "Sine.InOut",
    });

    for (const [cellX, cellY] of [
      [16.3, 4.2],
      [18.1, 5.5],
      [20.2, 3.7],
    ] as const) {
      const sprite = this.add
        .image(0, 0, "environment-firefly")
        .setDepth(RENDER_DEPTH.combatEffects - 2);
      this.ambientDecorations.push({ sprite, cellX, cellY, scale: 0.72 });
      this.layoutAmbientDecorations();
      this.tweens.add({
        targets: sprite,
        alpha: { from: 0.3, to: 1 },
        y: `-=${this.tile * 0.35}`,
        duration: Phaser.Math.Between(1_300, 2_100),
        delay: Phaser.Math.Between(0, 700),
        yoyo: true,
        repeat: -1,
        ease: "Sine.InOut",
      });
    }

    this.layoutAmbientDecorations();
  }

  private layoutAmbientDecorations(): void {
    for (const decoration of this.ambientDecorations) {
      decoration.sprite
        .setPosition(
          this.offsetX + decoration.cellX * this.tile,
          this.offsetY + decoration.cellY * this.tile,
        )
        .setScale(Math.max(0.35, (this.tile / 48) * decoration.scale));
    }
  }
  private visibilityChanged = (): void => {
    if (document.hidden && loadSave().settings.pauseWhenHidden) {
      this.state.setPaused(true);
    }
  };

  private scheduleResults(): void {
    if (this.resultsTransition) return;
    this.resultsTransition = this.time.delayedCall(900, () => {
      this.resultsTransition = null;
      this.scene.start("Results", { result: this.state.result() });
    });
  }

  private toggleDebugGrid(): void {
    this.registry.set("debug-grid", !this.registry.get("debug-grid"));
    this.drawGround();
  }

  private toggleDebugPath(): void {
    this.registry.set("debug-path", !this.registry.get("debug-path"));
    this.drawGround();
  }

  private stressSpawn(): void {
    this.state.stressSpawn(250);
  }
}
